// Promo service for API calls
import { PromoCode, PromoCodeResponse } from '../types';
import { fetchWithErrorHandling, guestPrefix } from './apiUtils';

// Raw API response uses snake_case — transform to camelCase PromoCode
function transformPromoCode(raw: Record<string, unknown>): PromoCode {
  return {
    id: raw.id as number | null,
    promo: raw.promo as string | null,
    promoType: (raw.promo_type ?? raw.promoType) as string | null,
    memo: raw.memo as string | null,
    amount: raw.amount as number | null,
    active: raw.active as boolean | null,
    autoapply: raw.autoapply as boolean | null,
    promoItemCode: (raw.promo_item_code ?? raw.promoItemCode ?? null) as string | null,
    promoCategory: (raw.promo_category ?? raw.promoCategory ?? null) as string | null,
    startDate: (raw.start_date ?? raw.startDate ?? null) as string | null,
    endDate: (raw.end_date ?? raw.endDate ?? null) as string | null,
    itemLimit: (raw.item_limit ?? raw.itemLimit ?? null) as number | null,
    creditcardonly: raw.creditcardonly as boolean | null,
    percent: raw.percent as number | null,
    excludeShipping: (raw.exclude_shipping ?? raw.excludeShipping ?? null) as boolean | null,
    displayBalance: (raw.display_balance ?? raw.displayBalance ?? null) as boolean | null,
    balance: raw.balance as number,
    clientId: (raw.client_id ?? raw.clientId ?? null) as number | null,
    categories: (raw.categories ?? []) as PromoCode['categories'],
    items: (raw.items ?? []) as PromoCode['items'],
  };
}

export class PromoService {
  static async getUserPromos(): Promise<PromoCodeResponse> {
    try {
      // Try API first
      try {
        const endpoint = `/api/v2${guestPrefix()}/users/me/promos`;
        const raw = await fetchWithErrorHandling<{ data: Record<string, unknown>[] }>(endpoint);
        return { data: raw.data.map(transformPromoCode) };
      } catch (apiError) {
        console.warn('API not available, falling back to local data:', apiError);

        // Fall back to local JSON file
        try {
          const { default: localPromoData } = await import('../data/promos.sample.data.json');
          return localPromoData as PromoCodeResponse;
        /* c8 ignore start */
        } catch (localError) {
          console.error('Failed to load local promo data:', localError);
          throw new Error('Unable to load promo data from both API and local data.');
        }
        /* c8 ignore stop */
      }
    /* c8 ignore start */
    } catch (error) {
      console.error('Failed to fetch promo data:', error);
      throw new Error('Unable to load promo data. Please try again later.');
    }
    /* c8 ignore stop */
  }
}
